import React, { Suspense, lazy, useEffect, useLayoutEffect, useRef } from "react";

import { Navigate, Route, Routes, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  silentRefetchUserInfo,
  logout,
} from "./features/auth/authSlice";
import ChunkErrorBoundary from "./components/ChunkErrorBoundary";
import ImpersonationBanner from "./components/ImpersonationBanner";

import "./App.css";

import ProtectedRoute from "./components/router/ProtectedRoute";
import { useBackNavigation } from "./hooks/useBackNavigation";
import {
  clearSubState,
  setContentState,
} from "./features/Content/contentTextSlice";
import { clearAllData } from "./features/data/dataSlice";
import { resetTaskState } from "./features/task/taskSlice";
import { logoutTraining } from "./features/Content/contentTrainingSlice";
import { persistor } from "./features/store";
import { useToast } from "./hooks/use-toast";
import { ordersApi } from "./features/Services/ordersServices";
import { statsApi } from "./features/Services/statsServices";
import { storeApi } from "./features/Services/storeServices";
import SubmissionStatusBanner from "./components/SubmissionStatusBanner";
import NotificationBanner from "./components/NotificationBanner";
import { RecordingPlayerProvider } from "./components/content/SalesRecording/RecordingPlayerContext";
import RecordingBanner from "./components/content/SalesRecording/RecordingBanner";
import UploadManagerBar from "./components/UploadManagerBar";
import InstallAppNudge from "./components/InstallAppNudge";
import PullToRefresh from "./components/PullToRefresh";
import { TooltipProvider } from "./components/ui/tooltip";
import { Toaster } from "./components/ui/sonner";
import { eventsApi } from "./features/Services/eventsServices";
import { userApi } from "./features/Services/userServices";
import { getSalesRepData } from "./features/sales/SalesActions";
import { readAvgRevenueCache, writeAvgRevenueCache, clearStoragePreservingPrefs, markJustDeepLinked } from "./lib/utils";
import { addFriendHomePath } from "./lib/addFriendHomeScreen";
import { nextPathFromSearch } from "./lib/nextPath";
import { hydrateLocalMenuPrefs } from "./lib/menuPrefs";
import { StatsPositions } from "./roles";
import EventGeofencePrompt from "./components/content/Events/EventGeofencePrompt";
import { registerPush } from "./pushNotifications";
import { resolveSection } from "./features/deepLink/sectionRoutes";
import { PREVIEW_MODE } from "./features/preview/previewMode";
import { noticeForSessionError, setLoginNotice } from "./lib/loginNotice";

// Route-level components — lazy-loaded so each route downloads only its
// own chunk. The public event-share link no longer pulls the entire
// 2 MB portal bundle on cold load.
const Index = lazy(() => import("./pages"));
const ForgetPassword = lazy(() => import("./pages/ForgetPassword"));
const ForgetUsername = lazy(() => import("./pages/ForgetUsername"));
const Home = lazy(() => import("./pages/Home"));
const PortalLink = lazy(() => import("./pages/PortalLink"));
const InviteEvent = lazy(() => import("./components/content/Events/InviteEvent"));
const EventSelfAdd = lazy(() => import("./components/content/Events/EventSelfAdd"));
const EventAttendanceCheckIn = lazy(() => import("./components/content/Events/EventAttendanceCheckIn"));
const FriendSelfAdd = lazy(() => import("./components/content/Friends/FriendSelfAdd"));
const EventClaim = lazy(() => import("./components/content/Events/EventClaim"));
const EventPublic = lazy(() => import("./components/content/Events/EventPublic"));
const TournamentSignup = lazy(() => import("./components/content/Tournament/TournamentSignup"));
const TournamentManagerGate = lazy(() =>
  import("./components/content/Tournament/TournamentPage").then((m) => ({ default: m.TournamentManagerGate })));
const TournamentPublicSignup = lazy(() => import("./components/content/Tournament/TournamentPublicSignup"));
const MatchLive = lazy(() => import("./components/content/Tournament/MatchLive"));
const TournamentEditorConsolePage = lazy(() => import("./components/content/Tournament/TournamentEditorConsolePage"));
const TournamentGuestView = lazy(() => import("./components/content/Tournament/TournamentGuestView"));
const TournamentJoinPartner = lazy(() => import("./components/content/Tournament/TournamentJoinPartner"));
const ArenaInvitePage = lazy(() => import("./components/content/Arena/ArenaInvitePage"));
const ArenaPage = lazy(() => import("./components/content/Arena/ArenaPage"));
const FriendVisionBoardPage = lazy(() => import("./components/content/Stats/VisionBoard/FriendVisionBoardPage"));
const PayrollSharePublic = lazy(() => import("./components/content/Payroll/payrollSales/PayrollSharePublic"));
const ChallengeSharePublic = lazy(() => import("./components/content/balance/ChallengeSharePublic"));
const EditEvent = lazy(() => import("./components/content/Events/EditEvent"));
const EventStats = lazy(() => import("./components/content/Events/EventStats"));
const EventAdminPage = lazy(() => import("./components/content/Events/EventAdminPage"));
const TournamentAdminLive = lazy(() => import("./components/content/Tournament/TournamentAdminLive"));
const RadialOrgPreview = lazy(() => import("./pages/RadialOrgPreview"));
const MassMessageAdmin = lazy(() => import("./pages/MassMessageAdmin"));
// Preview-only (#29): scoped, login-free entry to the Fantasy Sales League
// admin opened on the new Standings drill-in tab. Only mounted when
// REACT_APP_PREVIEW_MODE=true (the throwaway portal-ui-preview build); the prod
// CI build leaves the flag unset so the route below compiles out.
const FantasyLeagueAdmin = lazy(() =>
  import("./components/content/Tournament/FantasyLeagueAdmin")
);
// Preview-only (#50): scoped, login-free entry to the rep-facing Fantasy Sales
// "Spotlight" tab. Only mounted when REACT_APP_PREVIEW_MODE=true; the prod CI
// build leaves the flag unset so the route below compiles out.
const FantasySalesContent = lazy(() =>
  import("./components/content/Tournament/FantasySalesContent")
);
const Academy = lazy(() => import("./pages/Academy"));

const CreateEventRedirect = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setContentState("Create Event"));
  }, [dispatch]);
  return <Navigate to="/home" replace />;
};

// Deep-linkable sections (#20): /p/:section sets the target content state then
// renders /home, so a magic-link/notification lands the user straight on e.g.
// Payroll. Mirrors CreateEventRedirect above; an unknown slug just falls
// through to the default /home landing. No extra fetches — login speed unchanged.
const SectionDeepLink = () => {
  const dispatch = useDispatch();
  const { section } = useParams();
  const { search } = useLocation();
  // useLayoutEffect fires synchronously after DOM mutations, before any
  // useEffect — including the one inside <Navigate> that triggers the actual
  // navigation. This guarantees setContentState and markJustDeepLinked run
  // before Navigate unmounts this component, preventing the race where
  // Navigate's effect navigated away before the dispatch could fire.
  useLayoutEffect(() => {
    const target = resolveSection(section);
    if (target) {
      dispatch(setContentState(target));
      // Tell Home's landing resolver to skip the pinned-page redirect — we've
      // already set the intended destination and the pin must not override it.
      markJustDeepLinked();
    }
  }, [dispatch, section]);
  // Preserve the query string so a deep-link can carry a sub-view hint (e.g.
  // /p/fantasy-admin?fantasy=activity) through to the landing content.
  return <Navigate to={`/home${search || ""}`} replace />;
};

// The Add Friend home-screen app (lib/addFriendHomeScreen.js): its icon opens
// /add-friend, which lands on the portal's Home with the Add Friend form open.
// Same handshake as SectionDeepLink, so Home's pinned-landing redirect does not
// override it.
const AddFriendAppEntry = () => {
  const { search } = useLocation();
  useLayoutEffect(() => {
    markJustDeepLinked();
  }, []);
  return <Navigate to={addFriendHomePath(search)} replace />;
};

// Back on these two admin screens used to be a bare `navigate(-1)`. Neither
// route draws the portal nav, so on a fresh tab or an installed app — the way
// a shared /tournaments/admin/<id> link actually arrives — there is no entry
// behind the app and the control did nothing at all. Each now carries the
// destination its own button already names: "Events"
// (Events/EventAdminPage.jsx) and "Admin list" (Tournament/TournamentAdminLive.jsx).
// Both go through the /p/<slug> deep-link registry so the landing is the same
// page a magic link or a notification would land on.
const EventAdminPageRoute = () => {
  const { eventRecId } = useParams();
  const onBack = useBackNavigation("/p/events");
  return <EventAdminPage eventRecId={eventRecId} onBack={onBack} />;
};

const TournamentAdminLiveRoute = () => {
  const { id } = useParams();
  const onBack = useBackNavigation("/p/tournaments-admin");
  return <TournamentAdminLive recId={id} onBack={onBack} />;
};

// Catch-all for bare /tournaments/:id — notification "Open" buttons and
// standings copy-links point here. Redirect to /signup so the rep lands on
// the tournament detail/signup page with any search params preserved.
// Both routes sit inside TournamentManagerGate (thread 1a1a896a23cfff74): a
// person who manages the tournament gets the Manage view (the Tournament
// Console) from either link instead; everyone else gets this page unchanged.
const TournamentBaseRedirect = () => {
  const { id } = useParams();
  const location = useLocation();
  return <Navigate to={`/tournaments/${id}/signup${location.search}`} replace />;
};


function App() {
  const dispatch = useDispatch();
  const { toast } = useToast();
  // A protected route that found no session sent the visitor to /?next=<path>.
  // Once login fills the token in, that is where they belong — not /home.
  // Thread #1727.
  const { search: appSearch, pathname: appPathname } = useLocation();
  const loginDestination =
    appPathname === "/" ? nextPathFromSearch(appSearch) : null;
  const { token, userInfo, isImpersonating } = useSelector((state) => state.auth);
  const isImpersonatingRef = useRef(isImpersonating);
  const { tasksData } = useSelector((state) => state.contentTraining);
  const tasksDataRef = useRef(tasksData);

  useEffect(() => {
    isImpersonatingRef.current = isImpersonating;
  }, [isImpersonating]);

  useEffect(() => {
    tasksDataRef.current = tasksData;
  }, [tasksData]);

  // Re-seed this device's menu personalization (order/colors/pinned landing
  // page) from the durable server store as soon as we're authenticated. This is
  // what makes the settings "stick no matter how long has lapsed": even if iOS
  // Safari evicted localStorage, or the user is on a fresh device, the pinned
  // landing page is back in localStorage before Home's resolver reads it, and
  // the drawer opens with the saved order/colors. Best-effort + idempotent.
  useEffect(() => {
    if (!token) return;
    const userKey = userInfo?.["Employee ID"] || userInfo?.["Name"];
    if (!userKey) return;
    hydrateLocalMenuPrefs(userKey, token);
  }, [token, userInfo]);

  // After a chunk-load failure, lazyWithRetry.js navigates to the current URL
  // with ?_r= appended to force a fresh HTML fetch. Restore the content the
  // user was trying to reach (saved by Content.jsx into sessionStorage) so
  // they land directly on it instead of the default home screen.
  useEffect(() => {
    if (!token) return;
    const url = new URL(window.location.href);
    if (!url.searchParams.has("_r")) return;
    const target = sessionStorage.getItem("_chunkReloadReturn");
    sessionStorage.removeItem("_chunkReloadReturn");
    if (target) dispatch(setContentState(target));
  }, [token, dispatch]);

  // Register this device for web push once authenticated (feature #11, L3).
  // Best-effort and idempotent — registerPush no-ops without a token and
  // reuses any existing PushSubscription, so re-running on token change is safe.
  useEffect(() => {
    if (!token) return;
    registerPush(token);
  }, [token]);

  useEffect(() => {
    if (!token) {
      // Clear all API caches when logged out
      dispatch(ordersApi.util.resetApiState());
      dispatch(storeApi.util.resetApiState());
      dispatch(userApi.util.resetApiState())
      dispatch(eventsApi.util.resetApiState());
    }
  }, [token, dispatch]);

  
  useEffect(() => {
    if (!token) return;
    const firstName = userInfo?.["Name"];
    if (!firstName) return;

    const userKey =
      userInfo?.["Pestroutes ID"] ||
      userInfo?.["Employee ID"] ||
      firstName;
    const currentYear = new Date().getFullYear();

    // Cache already warm — skip the slow call entirely.
    if (readAvgRevenueCache(userKey, currentYear) !== null) return;

    let cancelled = false;
    dispatch(getSalesRepData({ name: firstName, year: currentYear }))
      .unwrap()
      .then((result) => {
        if (cancelled || result?.status !== "Success") return;
        const avg = result?.data?.AvgDailyRevenue || 0;
        writeAvgRevenueCache(userKey, currentYear, avg);
        localStorage.setItem(
          "isRepSuccess",
          JSON.stringify({ section: "trainingDetail", avgDailyRevenue: avg })
        );
      })
      .catch(() => {
        // will retry if needed.
      });

    return () => {
      cancelled = true;
    };
  }, [token, userInfo, dispatch]);

  useEffect(() => {
    if (!token) return;
    const positions = userInfo?.["Position (from Position)"];
    if (!Array.isArray(positions)) return;
    const canSeeStats = positions.some(
      (p) => typeof p === "string" && StatsPositions.includes(p.trim())
    );
    if (!canSeeStats) return;

    const STORAGE_KEY_SUBMITTED = "statsCompanyNew_submittedDates";
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const y = yesterday.getFullYear();
    const m = String(yesterday.getMonth() + 1).padStart(2, "0");
    const d = String(yesterday.getDate()).padStart(2, "0");
    const yesterdayStr = `${y}-${m}-${d}`;
    let tableRange = { startDate: yesterdayStr, endDate: yesterdayStr };
    try {
      const stored = localStorage.getItem(STORAGE_KEY_SUBMITTED);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.startDate && parsed?.endDate) tableRange = parsed;
      }
    } catch { /* fall back to yesterday */ }

    const currentYear = new Date().getFullYear();
    const cardRange = {
      startDate: `${currentYear}-01-01`,
      endDate: `${currentYear}-12-31`,
    };

    dispatch(
      statsApi.util.prefetch("getCompanySalesStatsNew", cardRange, { force: false })
    );
    if (
      tableRange.startDate !== cardRange.startDate ||
      tableRange.endDate !== cardRange.endDate
    ) {
      dispatch(
        statsApi.util.prefetch("getCompanySalesStatsNew", tableRange, { force: false })
      );
    }
  }, [token, userInfo, dispatch]);

  useEffect(() => {
    if (!token) return;

    const handleSessionExpiry = (err) => {
      // A Friend whose 2-day portal window closed gets the BFF's message on
      // the login screen, not a bare "Session Expired" (msg 1a1a9f1d57db2a7e).
      const notice = noticeForSessionError(err);
      // Quick Log: while the viewer is impersonating, NEVER auto-end the
      // session. Levi 2026-05-27 — the only way out is the manual Swap Back
      // button in ImpersonationBanner. A 401 on a stale or invalidated
      // impersonation token is dropped silently here; the user keeps the
      // page they're on until they click Swap Back themselves.
      if (isImpersonatingRef.current) {
        return;
      }

      toast({
        title: notice ? "Portal access paused" : "Session Expired",
        description: notice || "Your current session has ended. Please login again.",
        variant: "destructive",
        duration: notice ? 8000 : 3000,
      });

      setTimeout(() => {
        // Clear all caches before logout
        dispatch(ordersApi.util.resetApiState());
        dispatch(storeApi.util.resetApiState());
        dispatch(eventsApi.util.resetApiState());
        dispatch(userApi.util.resetApiState())

        dispatch(logout());
        dispatch(logoutTraining());
        dispatch(resetTaskState());
        dispatch(clearAllData());

        persistor.purge().then(() => {
          // Clears storage but preserves per-device UI prefs (menu order/
          // colors/pinned landing page, button positions, sales-recording
          // tags, payroll-admin prefs, dismissed banners) — see utils.js.
          clearStoragePreservingPrefs();
          setLoginNotice(notice);

          dispatch(setContentState(""));

          dispatch(clearSubState());
          sessionStorage.removeItem("contentSubStates");
        });
      }, 1500);
    };

    // Check session immediately on page load/reload.
    // Only trigger expiry on a confirmed 401 — network blips, Cloud Run cold
    // starts, and BFF 5xx errors are transient and must not force a re-login.
    dispatch(silentRefetchUserInfo())
      .unwrap()
      .catch((err) => { if (err?.status === 401) handleSessionExpiry(err); });

    // Continue with regular interval checks
    // Skip refetch when Inventory Inspection is open (tasksData is an array)
    const interval = setInterval(() => {
      if (Array.isArray(tasksDataRef.current)) {
        console.log("[Session Check] Skipped - Inventory Inspection is open");
        return;
      }
      if (sessionStorage.getItem("contentState") === "Tasks") {
        console.log("[Session Check] Skipped - Tasks page is open");
        return;
      }
      dispatch(silentRefetchUserInfo())
        .unwrap()
        .catch((err) => { if (err?.status === 401) handleSessionExpiry(err); });
    }, 7 * 60 * 1000);

    return () => clearInterval(interval);
  }, [dispatch, token]);

  return (
    <TooltipProvider delayDuration={200}>
      <RecordingPlayerProvider>
      <div className="bg-black min-h-screen">
        <Toaster theme="dark" position="bottom-center" />
        <UploadManagerBar />
        <ImpersonationBanner />
        <SubmissionStatusBanner />
        <NotificationBanner />
        <RecordingBanner />
        <InstallAppNudge />
        <PullToRefresh />
        <EventGeofencePrompt />
        <ChunkErrorBoundary>
          <Suspense fallback={<div className="min-h-screen bg-black" />}>
          <Routes>
              <Route path="/event-share/:recordId" element={<InviteEvent />} />
              <Route path="/event-share/:recordId/join" element={<EventSelfAdd />} />
              <Route path="/event-share/:recordId/check-in" element={<EventAttendanceCheckIn />} />
              {/* Thread #1640 — the Add Friend scan code's public page. No login:
                  the rep's code is what authorizes the add. */}
              <Route path="/friend-scan/:ownerRecId" element={<FriendSelfAdd />} />
              {/* Phase 11.2 — /events/admin/new is now a redirect to the
                  unified Create Event page (Phase 11.1). /events/admin/:id
                  still routes to the EventAdminPage (post-create admin view).
                  Both must mount BEFORE /events/:slug so /events/admin/*
                  doesn't fall through into the public-slug route. */}
              <Route path="/events/admin/new" element={<ProtectedRoute><CreateEventRedirect /></ProtectedRoute>} />
              <Route path="/events/admin/:eventRecId" element={<ProtectedRoute><EventAdminPageRoute /></ProtectedRoute>} />
              <Route path="/tournaments/admin/:id" element={<ProtectedRoute><TournamentAdminLiveRoute /></ProtectedRoute>} />
              {/* Phase 10.7 — public event_invite claim landing. The slug
                  is informational; the ?token= query param is the credential
                  the BFF actually uses. */}
              <Route path="/events/:slug/claim" element={<EventClaim />} />
              {/* Phase 10.8 — public spectator page (anonymous, no PII). */}
              <Route path="/events/:slug" element={<EventPublic />} />
              {/* Phase 10.6 — tournament sign-up walkthrough. Auth-gated
                  through the existing portal session. */}
              <Route path="/tournaments/:id/signup" element={
                <ProtectedRoute><TournamentManagerGate><TournamentSignup /></TournamentManagerGate></ProtectedRoute>
              } />
              {/* Phase 10.9 — public Manual Match Live page. tournament_match
                  magic-link in ?token=. Reusable for 1h — participants check in,
                  then later report. */}
              <Route path="/tournaments/:tournamentId/match/:matchId" element={<MatchLive />} />
              {/* Phase C.2 (link-based Friend Tournament Editor) — public,
                  no-login editor page. The ?et= k:"tedit" token is the only
                  credential (sent to the BFF as X-Editor-Token); NOT wrapped in
                  ProtectedRoute so a Friend with no portal account can manage
                  the tournament. The literal :id segment is non-"admin", so this
                  never shadows /tournaments/admin/:id (a ProtectedRoute).
                  Thread 352 step 2: renders the real creator console (minus the
                  Setup tab) under the editor token instead of the old bespoke
                  TournamentEditorPage, so the Friend view is in parity with what
                  the creator sees. */}
              <Route path="/tournaments/:id/editor" element={<TournamentEditorConsolePage />} />
              {/* Step 2 (Share Button feature) — public, no-login, READ-ONLY
                  guest tournament view. The ?gt= 24h guest token is the only
                  credential (re-verified + scope-locked by the BFF
                  getGuestTournament); NOT wrapped in ProtectedRoute so a
                  recipient with no portal account can view the bracket. The
                  literal :id segment is non-"admin", so this never shadows
                  /tournaments/admin/:id. Inert until Step 3 wires the Share
                  button that mints these links. */}
              <Route path="/tournaments/:id/guest" element={<TournamentGuestView />} />
              {/* Phase 5b-2 (doubles partner invite) — public, no-login "join my
                  doubles team" confirm page. The ?t= k:"tjoin" token is the only
                  credential (bakes in tournament+team+partner, re-verified +
                  scope-locked by the BFF public join endpoints); NOT wrapped in
                  ProtectedRoute so an invited partner with no portal account can
                  confirm. The literal :id segment is non-"admin", so this never
                  shadows /tournaments/admin/:id. */}
              <Route path="/tournaments/:id/join-partner" element={<TournamentJoinPartner />} />
              {/* Catch-all for bare /tournaments/:id — notification "Open"
                  buttons store this URL; redirect to /signup preserving ?params.
                  Must sit AFTER all /tournaments/:id/<sub> routes so specificity
                  wins: React Router v6 ranks 3-segment paths above 2-segment. */}
              <Route path="/tournaments/:id" element={<ProtectedRoute><TournamentManagerGate><TournamentBaseRedirect /></TournamentManagerGate></ProtectedRoute>} />
              {/* Phase 1 (MySQL-first signup) — public, no-login tournament
                  signup. The ?token= HMAC is the credential; NOT wrapped in
                  ProtectedRoute so a recruit with no portal account can join. */}
              <Route path="/tournament-signup/:id" element={<TournamentPublicSignup />} />
              {/* Phase 2b (Friend Arena access) — public, no-login Friend
                  sign-in. The ?token= is the Phase 2a Arena-invite HMAC over
                  :repRecId; NOT wrapped in ProtectedRoute so an invited Friend
                  with no portal account can sign in passwordlessly. */}
              <Route path="/arena-invite/:repRecId" element={<ArenaInvitePage />} />
              {/* Phase 2c (Friend Arena access) — public, login-free, READ-ONLY
                  Friend Arena view (FSL standings). Reads the Phase 2b Friend
                  session token from localStorage (or a ?token= deep link); NOT
                  wrapped in ProtectedRoute. */}
              <Route path="/arena" element={<ArenaPage />} />
              {/* Leadership Vision Board — public, no-login. Friends arriving
                  from the kindredsales.com leadership-CTA link land here after
                  auto-claim with dest=vision-board. Shows the real Override/MD
                  calculator (read-only) seeded from /public/portal/vision-board-demo. */}
              <Route path="/vision-board" element={<FriendVisionBoardPage />} />
              {/* Projected Earnings feature (thread 249) — public, no-login
                  read-only Payroll page share. The ?st= k:"pgshare" token is the
                  only credential, scoped to ONE rep + ONE page; NOT wrapped in
                  ProtectedRoute so a Friend with no portal account can view it. */}
              <Route path="/payroll-share/:pageType" element={<PayrollSharePublic />} />
              {/* Challenge leaderboard share (Item 8) — public, no-login.
                  The ?st= k:"chlboard" token is the credential; scoped to ONE
                  challenge for 24 hours. */}
              <Route path="/challenge-share/:id" element={<ChallengeSharePublic />} />
              <Route path="/forget-password" element={<ForgetPassword />} />
              <Route path="/forget-username" element={<ForgetUsername />} />
            <Route path="/" element={token ? <Navigate to={loginDestination || "/home"} replace /> : <Index />} />
            <Route path="/portal" element={<PortalLink />} />
            <Route path="/home" element={
              <div className="w-11/12 mx-auto">
                <ProtectedRoute><Home /></ProtectedRoute>
              </div>
            } />
            {/* #20 — deep-linkable sections. /p/payroll, /p/stats, etc. drop the
                user directly on that page. ProtectedRoute gates auth first, so a
                magic-link recipient lands on the section once their session is set. */}
            <Route path="/p/:section" element={
              <ProtectedRoute><SectionDeepLink /></ProtectedRoute>
            } />
            {/* The Add Friend home-screen app's start address (lib/addFriendHomeScreen.js). */}
            <Route path="/add-friend" element={
              <ProtectedRoute><AddFriendAppEntry /></ProtectedRoute>
            } />
            {/* Phase 1 of the Events-edit-page split: dedicated /edit route
                that mirrors the Create Event form for the editable common
                fields. Permission gate (host or granted editor) is enforced
                inside EditEvent so a deep-linked unauthorized user gets a
                clean refusal page, not a half-rendered form. */}
            <Route path="/manage-event/:recordId/edit" element={
              <ProtectedRoute><EditEvent /></ProtectedRoute>
            } />
            <Route path="/manage-event/:recordId/stats" element={
              <ProtectedRoute><EventStats /></ProtectedRoute>
            } />
            <Route path="/manage-event/:recordId" element={
              <ProtectedRoute><InviteEvent inPortal /></ProtectedRoute>
            } />
            <Route path="/radial-preview" element={
              <ProtectedRoute><RadialOrgPreview /></ProtectedRoute>
            } />
            {/* Phase 3 — Mass Message Admin lives on its own top-level route
                so it shares no parent render tree with the Mass Message page.
                v1 shipped this as a modal inside MassmessageTechsContent and
                its useGetRoleDetailsQuery subscription collided with Home →
                Content's render cascade (PR #1172, reverted in #1175). */}
            <Route path="/mass-message-admin" element={
              <ProtectedRoute><MassMessageAdmin /></ProtectedRoute>
            } />
            {PREVIEW_MODE && (
              <Route
                path="/preview/fantasy-admin"
                element={
                  <div className="w-11/12 mx-auto">
                    <FantasyLeagueAdmin initialMenu="standings" onBack={() => {}} />
                  </div>
                }
              />
            )}
            {PREVIEW_MODE && (
              <Route
                path="/preview/fantasy-spotlight"
                element={<FantasySalesContent initialTab="spotlight" onBack={() => {}} />}
              />
            )}
            {/* Academy — phone-first training re-skin (idea #12). Public route
                so the throwaway preview is clickable; the page self-gates: no
                token (or ?demo=1) renders an invented demo dataset, an
                authenticated session renders the rep's live training read-only.
                Grading/submit reuse the unchanged TrainingTaskDetails. */}
            <Route path="/academy" element={<Academy />} />
          </Routes>
          </Suspense>
        </ChunkErrorBoundary>
      </div>
      </RecordingPlayerProvider>
    </TooltipProvider>
  );
}

export default App;
